import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, FileCheck2, Eye, BadgeCheck, Banknote } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "PCI-DSS Compliant",
    desc: "Card data is processed on globally-certified, audited infrastructure.",
    testId: LANDING.trustBadgePci,
  },
  {
    icon: Lock,
    title: "Bank-grade Encryption",
    desc: "AES-256 at rest, TLS 1.3 in transit. Tokenised card storage.",
    testId: LANDING.trustBadgeEncryption,
  },
  {
    icon: BadgeCheck,
    title: "Verified Sellers Only",
    desc: "Every educator and professional completes KYC before money is released — no anonymous payouts.",
    testId: LANDING.trustBadgeRbi,
  },
  {
    icon: FileCheck2,
    title: "Transparent Pricing",
    desc: "Convenience fee is shown upfront — no surprises at checkout.",
  },
  {
    icon: Eye,
    title: "Instant Digital Receipts",
    desc: "Every payment gets a timestamped receipt for tax & records.",
  },
  {
    icon: Banknote,
    title: "Direct Bank Settlement",
    desc: "Funds reach recipient bank accounts via NEFT/IMPS — no third-party wallets.",
  },
];

export const Security = () => {
  return (
    <section
      id="security"
      data-testid={LANDING.securitySection}
      className="relative py-24 md:py-32 bg-[#0A0A0A] text-white overflow-hidden"
    >
      <div className="absolute -top-32 left-1/4 h-[400px] w-[400px] rounded-full bg-[#F25C2A]/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between gap-8 flex-wrap mb-14">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F25C2A]">
              · Security & Transparency
            </p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Built for big money.
              <br />
              <em className="italic text-[#F25C2A] font-normal">Audited for trust.</em>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> PCI-DSS
            </span>
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Verified Sellers
            </span>
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> ISO 27001
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              data-testid={p.testId}
              className="group rounded-3xl bg-white/[0.03] border border-white/10 p-7 hover:bg-white/[0.06] hover:border-white/20 transition-all"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F25C2A]/15 text-[#F25C2A] group-hover:bg-[#F25C2A] group-hover:text-white transition-colors">
                <p.icon size={22} />
              </span>
              <h3 className="mt-5 font-serif text-xl">{p.title}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
